/**
 * LongRangePlanProgressPanel
 * ----------------------------
 * Read-only progress card in the Coverage Tracker. One row per LRP
 * unit showing planned-vs-taught lessons, a simple visual progress
 * bar (no percentage), the last-taught date, and a four-state
 * status indicator:
 *
 *   - not_started   — no lessons generated yet
 *   - unscheduled   — lessons exist but none placed in the Planner
 *   - in_progress   — at least one scheduled (taught or upcoming)
 *   - complete      — every generated lesson has been taught
 *
 * Source of truth stays on the LRP canvas; this panel never edits.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarRange, ExternalLink, Layers } from "lucide-react";
import { api } from "../lib/api";

const SUBJECT_LABEL = {
  language: "Language",
  math: "Math",
  science: "Science",
  social_studies: "Social Studies",
  drama: "Drama",
  dance: "Dance",
  visual_art: "Visual Art",
  music: "Music",
  health: "Health & PE",
  french: "French",
};

// Phase 113.0 (Feb 2026) — the three real Ontario elementary reports.
const PERIOD_LABEL = {
  progress_report: "Progress Report · Sep–Nov",
  term_1: "Term 1 · Dec–Feb",
  term_2: "Term 2 · Feb–Jun",
};

const STATE_META = {
  not_started: {
    label: "Not started",
    bar: "bg-[#4a6984]/15",
    pill: "bg-[#4a6984]/12 text-[#4a6984]",
  },
  unscheduled: {
    label: "In progress — not yet scheduled",
    bar: "bg-amber-400/40",
    pill: "bg-amber-100 text-amber-900",
  },
  in_progress: {
    label: "In progress",
    bar: "bg-[#96ded1]",
    pill: "bg-[#96ded1]/35 text-[#1c2d3d]",
  },
  complete: {
    label: "Complete",
    bar: "bg-emerald-500",
    pill: "bg-emerald-100 text-emerald-900",
  },
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
};

const LrpUnitRow = ({ u }) => {
  const meta = STATE_META[u.state] || STATE_META.not_started;
  // Visual fill: taught / total, but if state is `unscheduled` we
  // still show a faint stripe so the row doesn't look identical to
  // "not started." If total=0, the bar stays empty.
  const fillPct =
    u.lessons_total > 0
      ? Math.max(
          u.state === "unscheduled" ? 6 : 0,
          Math.round((u.lessons_taught / u.lessons_total) * 100),
        )
      : 0;
  const lastTaught = fmtDate(u.last_taught_at);
  return (
    <div
      data-testid={`lrp-progress-row-${u.lrp_card_id}`}
      className="rounded-lg border border-[#4a6984]/12 bg-white p-4 hover:border-[#4a6984]/25 transition-colors"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="font-extrabold text-sm text-[#1c2d3d] tracking-tight truncate">
            {u.unit_name}
          </p>
          <div className="mt-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-[#4a6984]/70 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Layers className="h-3 w-3" />
              {SUBJECT_LABEL[u.subject_key] || u.subject_key}
            </span>
            <span className="opacity-60">·</span>
            <span className="inline-flex items-center gap-1">
              <CalendarRange className="h-3 w-3" />
              {PERIOD_LABEL[u.period] || u.period}
            </span>
          </div>
        </div>
        <span
          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${meta.pill}`}
          data-testid={`lrp-progress-state-${u.lrp_card_id}`}
        >
          {meta.label}
        </span>
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between text-xs font-semibold text-[#4a6984] mb-1">
          <span>
            {u.lessons_total === 0
              ? "No lessons generated yet"
              : `${u.lessons_taught} of ${u.lessons_total} lesson${u.lessons_total === 1 ? "" : "s"} taught`}
          </span>
          {lastTaught && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#4a6984]/70">
              Last taught {lastTaught}
            </span>
          )}
        </div>
        <div className="h-1.5 w-full rounded-full bg-[#4a6984]/10 overflow-hidden">
          <div
            className={`h-full rounded-full transition-[width] duration-300 ${meta.bar}`}
            style={{ width: `${fillPct}%` }}
          />
        </div>
      </div>

      {u.unit_activity_id && (
        <Link
          to={`/lesson/${u.unit_activity_id}`}
          className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#1c2d3d] hover:underline"
        >
          Open unit <ExternalLink className="h-2.5 w-2.5" />
        </Link>
      )}
    </div>
  );
};

const LongRangePlanProgressPanel = () => {
  const [rows, setRows] = useState(null); // null = loading, [] = ready, list = ready
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/long-range-plan/unit-progress")
      .then((r) => {
        if (!cancelled) setRows(r.data?.units || []);
      })
      .catch((err) => {
        if (cancelled) return;
        // 403 = non-Pro. Hide the panel silently rather than nagging.
        if (err?.response?.status === 403) setDenied(true);
        setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (denied) return null;
  if (rows === null) {
    return (
      <div className="ae-card p-4 text-sm text-[#4a6984]/70" data-testid="lrp-progress-loading">
        Loading Long Range Plan progress…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="ae-card p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="lrp-progress-empty">
        <div>
          <p className="font-extrabold text-base text-[#1c2d3d] tracking-tight">
            Long Range Plan progress
          </p>
          <p className="text-sm text-[#4a6984] mt-1">
            You haven't added any units to your Long Range Plan yet. Drop in your year's units to see teaching progress here.
          </p>
        </div>
        <Link
          to="/long-range-plan"
          data-testid="lrp-progress-empty-cta"
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-[#1c2d3d] text-white font-bold text-xs uppercase tracking-[0.1em] hover:bg-[#0f1c28]"
        >
          Open Long Range Plan
        </Link>
      </div>
    );
  }
  return (
    <div className="ae-card p-5" data-testid="lrp-progress-card">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <p className="font-extrabold text-base text-[#1c2d3d] tracking-tight">
            Long Range Plan progress
          </p>
          <p className="text-xs text-[#4a6984]/75 mt-0.5">
            Pulled from your year canvas — edit on the Long Range Plan, see progress here.
          </p>
        </div>
        <Link
          to="/long-range-plan"
          data-testid="lrp-progress-open"
          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#4a6984] hover:text-[#1c2d3d] underline decoration-dotted"
        >
          Open canvas
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((u) => (
          <LrpUnitRow key={u.lrp_card_id} u={u} />
        ))}
      </div>
    </div>
  );
};

export default LongRangePlanProgressPanel;
